import React from 'react';
import styled from '@emotion/native';
import { mint } from '@/shared/styles/colors';
import { BodyText, H3 } from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import { IsDesktopProps } from '@/shared/styles/types';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { useInstantStylingToggle } from '@/shared/hooks/useInstantStylingToggle';
import { IS_WEB } from '@/shared/constants/utility';

interface Props {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const StylistIntro = ({ setStep }: Props) => {
  const router = useRouter();
  const isDesktop = useIsDesktop();

  const { isInstantStylingActive, toggleInstantStyling } =
    useInstantStylingToggle();

  const onPressLocateClient = () => {
    if (IS_WEB && !isDesktop) {
      // TODO (ES-472): show message to open in desktop or mobile app
      alert(
        'Please use desktop or the mobile app as video calls are not supported on mobile web.',
      );
      return;
    }

    !isInstantStylingActive && toggleInstantStyling();
    setStep((prev) => prev + 1);
  };

  const onPressLearnMore = () => {
    router.push(ROUTES.HELP.path);
  };

  return (
    <ContentContainer isDesktop={isDesktop}>
      <TopContentContainer>
        <IntroImageContainer>
          <IntroImage
            source={require('@/assets/images/InstantStylingStylistIntro.png')}
          />
        </IntroImageContainer>
        <Title>{`Go Live,${isDesktop ? ' ' : `\n`}Get Booked`}</Title>
        <MainText isDesktop={isDesktop} style={{ marginBottom: 24 }}>
          {`Toggle your availability with a tap. When you're live, clients can instantly book you for real-time styling.\n\nP.S. Your status will remain “On & Available” for up to 24 hours, or until you toggle it off.`}
        </MainText>
        <Button
          text="Locate a Client Now"
          onPress={onPressLocateClient}
          size="medium"
          customStyle={locateClientButtonStyle}
        />
      </TopContentContainer>
      <MainText style={{ fontSize: 12, marginBottom: 12 }}>
        Learn more about{' '}
        <BoldLinkText onPress={onPressLearnMore}>Instant Styling</BoldLinkText>
      </MainText>
    </ContentContainer>
  );
};

export default StylistIntro;

const ContentContainer = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  flex: 1,
  width: '100%',
  backgroundColor: mint,
  borderRadius: isDesktop ? 12 : 0,
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 56,
  paddingBottom: 36,
}));

const TopContentContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const Title = styled(H3)({
  textAlign: 'center',
  textTransform: 'uppercase',
  marginBottom: 16,
});

const IntroImageContainer = styled.View({
  width: 236,
  height: 170,
  borderRadius: 85,
  marginBottom: 30,
});

const IntroImage = styled.Image({
  width: '100%',
  height: '100%',
  resizeMode: 'contain',
});

const MainText = styled(BodyText)<IsDesktopProps>(({ isDesktop }) => ({
  maxWidth: 390,
  fontSize: 16,
  lineHeight: 18,
  textAlign: 'center',
  paddingHorizontal: isDesktop ? 0 : 42,
}));

const locateClientButtonStyle = {
  width: 300,
  paddingHorizontal: 24,
  marginBottom: 48,
};

const BoldLinkText = styled(BodyText)({
  fontSize: 12,
  fontWeight: 'bold',
  textDecorationLine: 'underline',
});
